import { Button, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { FiArrowRight, FiXCircle } from "react-icons/fi";
import { type BuyerBidDetailDto, useGetBidForBuyerQuery } from "../../../autogen/bff-api";
import { RejectBidButton } from "../bid-detail/BidRejectionActionCard";
import { useUndoRejectionDialog } from "../bid-detail/useUndoRejectionDialog";
import { getBidMinimumRequirementProgress } from "../bid-detail/bidMinimumRequirements";

interface Props {
  eventId: string;
  bidId: string;
  onRejected: () => void;
  allAssessed: boolean;
  onGoToEvaluation: () => void;
}

export const MinimumRequirementsFocusActions = ({
  eventId,
  bidId,
  onRejected,
  allAssessed,
  onGoToEvaluation,
}: Props) => {
  const { data: detail } = useGetBidForBuyerQuery({ eventId, bidId });
  if (!detail) return null;
  return (
    <Actions
      detail={detail}
      allAssessed={allAssessed}
      onRejected={onRejected}
      onGoToEvaluation={onGoToEvaluation}
    />
  );
};

const Actions = ({
  detail,
  allAssessed,
  onRejected,
  onGoToEvaluation,
}: {
  detail: BuyerBidDetailDto;
  allAssessed: boolean;
  onRejected: () => void;
  onGoToEvaluation: () => void;
}) => {
  const { t } = useTranslation();
  const rejection = useUndoRejectionDialog(detail);

  const { reviewed, total } = getBidMinimumRequirementProgress(detail);
  const isRejected = rejection.isRejected;

  return (
    <>
      {rejection.modal}
      {allAssessed && (
        <Button
          data-testid="focus-go-to-evaluation"
          variant="outline"
          colorScheme="green"
          bg="smSuccessSoft"
          rightIcon={<FiArrowRight />}
          onClick={onGoToEvaluation}
        >
          {t("Go to the evaluation")}
        </Button>
      )}
      {isRejected ? (
        <Button
          data-testid="focus-minreq-unreject"
          colorScheme="red"
          variant="outline"
          leftIcon={<FiXCircle />}
          isLoading={rejection.isLoading}
          isDisabled={!rejection.canRevoke}
          onClick={rejection.canRevoke ? () => rejection.open() : undefined}
        >
          {rejection.canRevoke ? t("Rejected — undo") : t("Rejected")}
        </Button>
      ) : (
        <RejectBidButton
          detail={detail}
          width="auto"
          label={String(t("Reject the bid"))}
          onRejected={onRejected}
        />
      )}
      <Text fontSize="xs" color="smMuted">
        {isRejected
          ? t("This bid is rejected.")
          : t("{{reviewed}} of {{total}} minimum requirements assessed for {{supplier}}. Everything saves as you go.", {
              reviewed,
              total,
              supplier: detail.ownerName,
            })}
      </Text>
    </>
  );
};
